// -----------------------------------------------------------------------------
// panels/index.js
//
// PANEL_MAP used by ActionPanel.jsx — maps item_type → panel component.
// Eagerly imported so Suspense does not stall in Vite dev-server HMR contexts
// where re-evaluation of this module (due to timestamp-busted cache) would
// create fresh lazy() instances whose Promises never resolve.
//
// Adding a new panel:
//   1. Create the .jsx file in this directory.
//   2. Import it below and add to PANEL_MAP.
// -----------------------------------------------------------------------------
import LogCallPanel from './LogCallPanel';
import ScheduleReminderPanel from './ScheduleReminderPanel';
import AddNotePanel from './AddNotePanel';
import NextActionPanel from './NextActionPanel';
import FollowUpPanel from './FollowUpPanel';
import WhatsAppPanel from './WhatsAppPanel';
import SMSPanel from './SMSPanel';
import SendConsentPanel from './SendConsentPanel';
import SendPaymentLinkPanel from './SendPaymentLinkPanel';
import SimpleFieldPanel from './SimpleFieldPanel';
import UploadDocumentPanel from './UploadDocumentPanel';
import ConfirmationPanel from './ConfirmationPanel';
import YesNoTogglePanel from './YesNoTogglePanel';
import LeadStatusChangedPanel from './LeadStatusChangedPanel';
import InfoPanel from './InfoPanel';

/**
 * Maps checklist item_type → panel component.
 * EmailActionBody is handled separately in ActionPanel.jsx (EMAIL_ACTION_TYPES set).
 */
export const PANEL_MAP = {
    // Wave A
    PHONE_CALLED:        LogCallPanel,
    LOG_PHONE_CALL:      LogCallPanel,
    SCHEDULE_REMINDER:   ScheduleReminderPanel,
    ADD_NOTE:            AddNotePanel,
    // Follow-up items (LEAD_NEXT_ACTION on p92; OPEN_FOLLOW_UP on other practices)
    // route to the Call/WA/Email/Note channel picker.  BOOK_APPOINTMENT keeps
    // NextActionPanel for genuine forward-status outcomes.
    LEAD_NEXT_ACTION:    FollowUpPanel,
    OPEN_FOLLOW_UP:      FollowUpPanel,
    BOOK_APPOINTMENT:    NextActionPanel,
    // Wave B
    WHATSAPP_SENT:       WhatsAppPanel,
    SMS_SENT:            SMSPanel,
    SEND_CONSENT_FORM:   SendConsentPanel,
    // Wave C
    SEND_PAYMENT_LINK:   SendPaymentLinkPanel,
    FREE_TEXT:           SimpleFieldPanel,
    MULTIPLE_CHOICE:     SimpleFieldPanel,
    CURRENCY_AMOUNT:     SimpleFieldPanel,
    PAYMENT_METHOD:      SimpleFieldPanel,
    STAFF_PICKER:        SimpleFieldPanel,
    DATE_FIELD:          SimpleFieldPanel,
    SIMPLE_DATE:         SimpleFieldPanel,
    UPLOAD_DOCUMENT:     UploadDocumentPanel,
    // Wave D (2026-05-29): types that previously fell through to the
    // "isn't wired up yet" stub. Each panel does its own API call (mirroring the
    // matching cell in TaskChecklistField.jsx) then calls onComplete as a refresh
    // signal. Additive — for the attended popup, CONFIRMATION / YES_NO_TOGGLE /
    // LINKED_CHECKLIST stay in INLINE_HANDLED_TYPES (never open ActionPanel), so
    // these entries only take effect where the panel actually opens (e.g. the hub).
    CONFIRMATION:        ConfirmationPanel,
    YES_NO_TOGGLE:       YesNoTogglePanel,
    LEAD_STATUS_CHANGED: LeadStatusChangedPanel,
    LINKED_CHECKLIST:    InfoPanel,
    START_TREATMENT:     InfoPanel,
    COMPLETE_TREATMENT:  InfoPanel,
};

/**
 * Item types handled inline on the row — no panel should open for these.
 */
export const INLINE_HANDLED_TYPES = new Set([
    'YES_NO_TOGGLE',
    'CONFIRMATION',
    'LINKED_CHECKLIST',
]);
